import { useChat, type UseChatHelpers } from '@ai-sdk/react';
import { getCurrentUser } from '@bridebook/toolbox/src/api/auth/getCurrentUser';
import { getRecentActivity } from '@bridebook/web/lib/ai-planner/activity/activity-log';
import { COACH_OPENER_MESSAGE } from '@bridebook/web/lib/ai-planner/coach/opener-intent';
import {
  logDataPart,
  logModelToolCall,
  logOnboardingExit,
  logPlannerThreadCleared,
  logTurnLog,
  readClientActionLog,
} from '@bridebook/web/lib/ai-planner/debug/client-log';
import { PLANNER_DEBUG } from '@bridebook/web/lib/ai-planner/debug/format';
import { readPlannerServerState } from '@bridebook/web/lib/ai-planner/debug/read-server-state';
import { deriveInterruptedRequestIds } from '@bridebook/web/lib/ai-planner/interrupted-requests';
import { withCreatedAt } from '@bridebook/web/lib/ai-planner/message-time';
import {
  clearPlannerHistory,
  plannerHistoryQueryKey,
  usePlannerHistory,
  type PlannerClearOptions,
  type PlannerHistory,
} from '@bridebook/web/lib/ai-planner/hooks/use-planner-history';
import { usePlannerFullStory } from '@bridebook/web/lib/ai-planner/hooks/use-planner-fullstory';
import { parseRateLimitFromResponse } from '@bridebook/web/lib/ai-common/parse-rate-limit';
import type { AIRateLimit } from '@bridebook/web/lib/ai-common/rate-limit-types';
import { useRateLimitExpire } from '@bridebook/web/lib/ai-common/use-rate-limit-clock';
import {
  getIsAIPlannerAvailable,
  getIsAIPlannerKickoffPending,
  getIsAIPlannerPanelOpen,
} from '@bridebook/web/lib/ai-planner/selectors';
import {
  onboardingExited,
  plannerReplyFinished,
  setAIPlannerKickoffPending,
} from '@bridebook/web/lib/ai-planner/slice';
import { plannerRequestContext } from '@bridebook/web/lib/ai-planner/request-context';
import type { ExecuteClientActionsOutput } from '@bridebook/web/lib/ai-planner/client-actions/schema';
import {
  holdsClientActionsCall,
  pendingEnquiryVenues,
  withClientActionsOutput,
} from '@bridebook/web/lib/ai-planner/enquiry-proposals';
import { getMissingEnquiryDetails } from '@bridebook/web/lib/enquiries/selectors';
import { lookupChecklistTasks } from '@bridebook/web/lib/ai-planner/checklist-lookup';
import type { ResolvedChecklist } from '@bridebook/web/lib/ai-planner/planning/blueprint/types';
import { BudgetActionTypes } from '@bridebook/web/lib/budget/action-types';
import { ChecklistActionTypes } from '@bridebook/web/lib/checklist/action-types';
import { useSelector, useStore } from '@bridebook/web/lib/utils';
import { getWeddingId } from '@bridebook/web/lib/weddings/selectors';
import type { TurnReceipt } from '@bridebook/web/lib/ai-planner/writes/receipt';
import { useDispatch } from 'react-redux';
import { takeLastUserMessage } from '@bridebook/web/lib/ai-planner/take-last-user-message';
import type { PlannerUIMessage } from '@bridebook/web/lib/ai-planner/types';
import type { WeddingContextSummary } from '@bridebook/web/lib/ai-planner/wedding-context-summary';
import { useQueryClient } from '@tanstack/react-query';
import { DefaultChatTransport, lastAssistantMessageIsCompleteWithToolCalls } from 'ai';
import { useRouter } from 'next/router';
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { WeddingContextSync } from './wedding-context-sync';

type Helpers = UseChatHelpers<PlannerUIMessage>;

// Load state of the planning summary, and the attempt that produced it.
type PlanningContextState = { ready: boolean; failed: boolean; attempt: number };

// The couple's answer to a consent card, kept for the session.
export type ToolCallDecision = 'approved' | 'declined';

type AIPlannerContextValue = {
  sendMessage: Helpers['sendMessage'];
  setMessages: Helpers['setMessages'];
  status: Helpers['status'];
  error: Helpers['error'];
  stop: Helpers['stop'];
  // Resolves a client-side tool call (e.g. `executeClientActions`).
  addToolResult: Helpers['addToolResult'];
  // Re-runs the last turn after a failure (retry from the error banner).
  regenerate: Helpers['regenerate'];
  // Takes the failed message back out of the thread and hands its text to the composer.
  editLastMessage: () => void;
  // Text the composer should show next, from an edit; null once taken.
  restoredDraft: string | null;
  clearRestoredDraft: () => void;
  // True while the stored thread might still appear. The empty state must not show then,
  // or it flashes before the history loads.
  historyLoading: boolean;
  // Whether the conversation has any message yet.
  hasMessages: boolean;
  // Whether the planning summary is ready: a profile exists, and the checklist and budget
  // sources finished their first load. Sending waits for it, typing does not.
  contextReady: boolean;
  // Whether one of those sources failed. The panel then offers a retry instead of waiting.
  contextFailed: boolean;
  // Not ready yet, and no source has failed.
  contextLoading: boolean;
  // Fetches the planning summary's sources again after a failure.
  retryContext: () => void;
  // Sends the opener, so the coach speaks first. A chip tap passes its id, the kickoff after
  // signup passes none.
  startCheckin: (chipId?: string) => void;
  // The current messages at call time. Stable, so calling it does not subscribe to the stream.
  getMessages: () => PlannerUIMessage[];
  // The planning summary the next request sends, at call time. Undefined until it loads.
  getWeddingContext: () => WeddingContextSummary | undefined;
  /**
   * Whether a tool call arrived on this session's stream. Only these can run. A pending call
   * from the stored thread has no stream to return a result to, so it shows as interrupted.
   */
  isLiveToolCall: (toolCallId: string) => boolean;
  // The couple's consent decision for a batch, kept for the session.
  getToolCallDecision: (toolCallId: string) => ToolCallDecision | null;
  recordToolCallDecision: (toolCallId: string, decision: ToolCallDecision) => void;
  // Records a client-actions result on whichever message holds the call.
  settleClientActions: (toolCallId: string, output: ExecuteClientActionsOutput) => void;
  // What the last turn wrote (from `data-turnReceipt`); null when it wrote nothing.
  turnReceipt: TurnReceipt | null;
  // Id of this conversation, stable for the page load (the analytics session).
  chatId: string;
  // Set once the quota is spent; cleared when the window resets.
  rateLimit: AIRateLimit | null;
  // Scroll offset of a results row, kept per coach message.
  getCarouselScroll: (key: string) => number | undefined;
  setCarouselScroll: (key: string, offset: number) => void;
};

/**
 * The part of the conversation that changes on every streamed chunk. It has its own context,
 * so only the thread re-renders per chunk. Other consumers re-render only between turns.
 */
type AIPlannerMessagesValue = {
  messages: Helpers['messages'];
  // Ids of the couple's messages whose action batch stopped before it resolved.
  interruptedRequestIds: ReadonlySet<string>;
};

// Max rate of streamed updates to React. Tokens arrive faster than the screen needs them,
// and every update re-renders the thread.
const STREAM_RENDER_THROTTLE_MS = 50;

const AIPlannerCtx = createContext<AIPlannerContextValue | null>(null);
const AIPlannerMessagesCtx = createContext<AIPlannerMessagesValue | null>(null);

/**
 * Whether the couple has opened the planner: the panel or the planner page.
 * Computed during render, so a surface finds the conversation on its first render.
 * Stays true for the session, so the conversation does not unmount with the surface.
 */
const usePlannerEngaged = (): boolean => {
  const isPanelOpen = useSelector(getIsAIPlannerPanelOpen);
  const isAvailable = useSelector(getIsAIPlannerAvailable);
  const { pathname } = useRouter();
  const reached = isPanelOpen || (pathname === '/[market]/planner' && isAvailable);

  const [everReached, setEverReached] = useState(false);
  useEffect(() => {
    if (reached) setEverReached(true);
  }, [reached]);

  return reached || everReached;
};

/**
 * Mounts the conversation above the route content, so a turn survives navigation.
 * The tree has the same shape before and after the couple opens the planner, so the page
 * does not remount on the first open. Until then the conversation is idle: the stored
 * thread is not fetched and the wedding data is not subscribed to.
 */
export const AIPlannerProvider = ({ children }: { children: ReactNode }) => {
  usePlannerFullStory();
  const engaged = usePlannerEngaged();

  return <PlannerConversation engaged={engaged}>{children}</PlannerConversation>;
};

const PlannerConversation = ({ engaged, children }: { engaged: boolean; children: ReactNode }) => {
  // The summary sent to the route. The sync component keeps it current, so the provider
  // does not subscribe to wedding data.
  const weddingContextRef = useRef<WeddingContextSummary | undefined>(undefined);
  // The checklist the task lookup answers from, kept current by the sync component below.
  const resolvedChecklistRef = useRef<ResolvedChecklist | null>(null);
  // `onToolCall` is bound before `useChat` returns this helper, so it goes through a ref.
  const addToolResultRef = useRef<Helpers['addToolResult'] | null>(null);
  // Set below, once the chat and the decision record exist.
  const pendingEnquiriesRef = useRef<() => string[]>(() => []);
  // One state, because these change together: the sync reports ready and failed at once,
  // and a retry clears the failure and starts the next attempt at once.
  const [context, setContext] = useState<PlanningContextState>({
    ready: false,
    failed: false,
    attempt: 0,
  });
  const { ready: contextReady, failed: contextFailed, attempt: contextAttempt } = context;
  const contextLoading = !contextReady && !contextFailed;
  // Returns the same object when nothing changed, so it causes no re-render.
  const onContextStatus = useCallback(
    (status: { ready: boolean; failed: boolean }) =>
      setContext((current) =>
        current.ready === status.ready && current.failed === status.failed
          ? current
          : { ...current, ...status },
      ),
    [],
  );
  const weddingId = useSelector(getWeddingId) || undefined;
  const queryClient = useQueryClient();

  // The store, not a subscription: page and search state are read only when a message
  // is sent, so navigation and search fetches do not re-render the conversation.
  const store = useStore();

  const [turnReceipt, setTurnReceipt] = useState<TurnReceipt | null>(null);
  const [rateLimit, setRateLimit] = useState<AIRateLimit | null>(null);
  // Cleared the moment the window resets, so the composer comes back on its own.
  useRateLimitExpire(
    rateLimit,
    useCallback(() => setRateLimit(null), []),
  );

  // Per-conversation id (Langfuse sessionId). Not persisted: each page load starts a new one.
  const chatIdRef = useRef<string>(crypto.randomUUID());

  const transport = useMemo(
    () =>
      new DefaultChatTransport<PlannerUIMessage>({
        api: '/api/ai-planner',
        body: () => ({
          weddingContext: weddingContextRef.current,
          chatId: chatIdRef.current,
          recentActivity: getRecentActivity(),
          // Lets the server put the activity timestamps on its own clock.
          clientNow: Date.now(),
          ...plannerRequestContext(store.getState()),
          enquiry: {
            missing: getMissingEnquiryDetails(store.getState()),
            pending: pendingEnquiriesRef.current(),
          },
        }),
        fetch: async (input, init) => {
          const currentUser = await getCurrentUser();
          const token = currentUser ? await currentUser.getIdToken() : null;
          const headers = new Headers(init?.headers);
          if (token) {
            headers.set('Authorization', `Bearer ${token}`);
          }
          const response = await fetch(input, { ...init, headers });
          if (response.status === 429) {
            const parsed = await parseRateLimitFromResponse(response);
            if (parsed) {
              setRateLimit(parsed);
            }
          }
          return response;
        },
      }),
    [store],
  );

  // Ids of tool calls that arrived on this session's stream. Only these can run. A pending
  // call from restored history has no stream to return a result to, and running it after a
  // reload would replay it.
  // Append-only: removing an id would bring back races, such as a card remounting mid-turn.
  // The conversation length cap bounds its size.
  const streamedToolCallIdsRef = useRef<Set<string>>(new Set());
  // The onboarding exit is dispatched to the app once per session, however many turns say it.
  const exitedRef = useRef(false);
  // An exit the route reported, held until its reply finishes
  const pendingExitRef = useRef<{ settled: string[]; asked: string[]; at: number } | null>(null);
  const dispatch = useDispatch();
  const isLiveToolCall = useCallback(
    (toolCallId: string) => streamedToolCallIdsRef.current.has(toolCallId),
    [],
  );

  // Scroll offsets of the results rows, one per coach message. Kept here because a row
  // unmounts when the couple opens a supplier and remounts when they come back.
  const carouselScrollRef = useRef<Map<string, number>>(new Map());
  const getCarouselScroll = useCallback((key: string) => carouselScrollRef.current.get(key), []);
  const setCarouselScroll = useCallback((key: string, offset: number) => {
    carouselScrollRef.current.set(key, offset);
  }, []);

  // A batch's actions run at most once: strict-mode double effects and remounts read the
  // decision from here, and a remount does not reopen a settled consent card.
  const decisionsRef = useRef<Map<string, ToolCallDecision>>(new Map());
  const getToolCallDecision = useCallback(
    (toolCallId: string) => decisionsRef.current.get(toolCallId) ?? null,
    [],
  );
  const recordToolCallDecision = useCallback((toolCallId: string, decision: ToolCallDecision) => {
    decisionsRef.current.set(toolCallId, decision);
  }, []);

  const {
    messages,
    sendMessage: send,
    setMessages,
    status,
    error,
    stop,
    addToolResult,
    regenerate,
    clearError,
  } = useChat<PlannerUIMessage>({
    transport,
    experimental_throttle: STREAM_RENDER_THROTTLE_MS,
    // A client-resolved tool such as executeClientActions pauses the turn. Once
    // the actions card calls addToolResult, submit so the model continues.
    sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithToolCalls,
    onFinish: ({ messages, isAbort, isDisconnect, isError }) => {
      // A reply the couple can read: not cut short, and not about to continue on its own
      if (isAbort || isDisconnect || isError) return;
      if (lastAssistantMessageIsCompleteWithToolCalls({ messages })) return;
      dispatch(plannerReplyFinished());
      if (pendingExitRef.current) {
        dispatch(onboardingExited(pendingExitRef.current));
        pendingExitRef.current = null;
      }
    },
    onToolCall: ({ toolCall }) => {
      streamedToolCallIdsRef.current.add(toolCall.toolCallId);
      logModelToolCall(toolCall.toolName, toolCall.input);
      // The task lookup runs in the browser, which has the checklist and its live done state.
      // The turn then continues on its own.
      if (toolCall.toolName === 'findChecklistTasks') {
        void addToolResultRef.current?.({
          tool: 'findChecklistTasks',
          toolCallId: toolCall.toolCallId,
          output: lookupChecklistTasks(resolvedChecklistRef.current, toolCall.input),
        });
      }
    },
    onData: (dataPart) => {
      if (dataPart.type === 'data-turnReceipt') {
        // One receipt per turn: `sendMessage` clears the slate as the next turn opens.
        setTurnReceipt(dataPart.data);
      }
      if (dataPart.type === 'data-onboardingStage') {
        // Dispatched once per session: the route sends `exited` on every turn after the exit,
        // and the app only needs the first one.
        const stage = dataPart.data;
        if (stage.stage === 'exited' && !exitedRef.current) {
          exitedRef.current = true;
          const exit = {
            settled: [...stage.settled],
            asked: [...stage.asked],
            at: stage.exitedAt ?? Date.now(),
          };
          logOnboardingExit(exit);
          // A turn paused on one of the coach's own actions has not finished its reply yet
          pendingExitRef.current = exit;
        }
        return;
      }
      if (dataPart.type === 'data-turnLog') {
        logTurnLog(dataPart.data);
        return;
      }
      logDataPart(dataPart.type, dataPart.data);
    },
  });

  addToolResultRef.current = addToolResult;

  // Depends on the list only: a new registry entry always comes with a message change,
  // so a new list identity covers both.
  const interruptedRequestIds = useMemo(
    () => deriveInterruptedRequestIds(messages, isLiveToolCall),
    [messages, isLiveToolCall],
  );

  // Seeds the stored thread into the chat once it loads, only if the chat is still empty.
  // If the couple already sent a message, history does not overwrite it.
  // No wedding id until the planner opens, so the query stays disabled
  const { history, pending: historyPending } = usePlannerHistory(engaged ? weddingId : undefined);
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (hydratedRef.current || !history.loaded || history.messages.length === 0) return;
    hydratedRef.current = true;
    setMessages((current) => (current.length === 0 ? history.messages : current));
  }, [history, setMessages]);

  // An exit from an earlier visit reaches the store too, since the route reports it per turn
  useEffect(() => {
    if (history.onboardingExitedAt === null || exitedRef.current) return;
    exitedRef.current = true;
    dispatch(onboardingExited({ settled: [], asked: [], at: history.onboardingExitedAt }));
  }, [history.onboardingExitedAt, dispatch]);

  // Also loading for the one paint where history has loaded but the seed effect has not run.
  const historyLoading = historyPending || (messages.length === 0 && history.messages.length > 0);
  const hasMessages = messages.length > 0;

  // The latest list behind a stable function.
  const messagesRef = useRef(messages);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);
  const getMessages = useCallback(() => messagesRef.current, []);
  const getWeddingContext = useCallback(() => weddingContextRef.current, []);

  // A one-shot handoff: the composer shows it once and clears it.
  const [restoredDraft, setRestoredDraft] = useState<string | null>(null);
  const clearRestoredDraft = useCallback(() => setRestoredDraft(null), []);
  const editLastMessage = useCallback(() => {
    const taken = takeLastUserMessage(messagesRef.current);
    if (!taken) return;
    setMessages(taken.messages);
    clearError();
    setRestoredDraft(taken.draft);
  }, [setMessages, clearError]);

  // A decided proposal is no longer pending, even before its result lands.
  pendingEnquiriesRef.current = () =>
    pendingEnquiryVenues(
      messagesRef.current,
      (toolCallId) => isLiveToolCall(toolCallId) && !decisionsRef.current.has(toolCallId),
    );

  // The SDK only writes a tool result to the newest message. A result for an older call is
  // written in place, and the coach reads it from the thread on the next turn.
  const settleClientActions = useCallback(
    (toolCallId: string, output: ExecuteClientActionsOutput) => {
      // Read the chat's current list: the rendered one can lag behind a new message, and a
      // result written to the wrong message is lost.
      let read = false;
      let onNewest = false;
      setMessages((current) => {
        read = true;
        const last = current.at(-1);
        onNewest = Boolean(last && holdsClientActionsCall(last, toolCallId));
        return onNewest ? current : withClientActionsOutput(current, toolCallId, output);
      });
      // If the list was not read yet, also run the SDK write: it covers the newest message,
      // and the update above covers any other.
      if (!read || onNewest) {
        void addToolResult({ tool: 'executeClientActions', toolCallId, output });
      }
    },
    [addToolResult, setMessages],
  );

  // Debug console helpers, behind the same flag as the turn logs.
  // Clearing needs all three steps: the next turn sends the browser's list as history, and
  // the cached read seeds the chat again on the next open.
  // `{ loop: true, memory: true }` also clears stored facts, for a true first meeting.
  // Add `{ wedding: true }` for a fresh account, then reload.
  useEffect(() => {
    if (!PLANNER_DEBUG || typeof window === 'undefined') return;
    const target = window as unknown as Record<string, unknown>;
    target.__aiPlannerClearThread = async (options?: PlannerClearOptions) => {
      const result = await clearPlannerHistory(options);
      setMessages([]);
      queryClient.setQueryData<PlannerHistory>(plannerHistoryQueryKey(weddingId), {
        messages: [],
        loaded: true,
        onboardingExitedAt: null,
      });
      logPlannerThreadCleared(result);
      return result;
    };
    // Server-side planner state that does not reach the browser otherwise.
    target.__aiPlannerDebugState = () => readPlannerServerState();
    // What the client action batches did, which is not visible anywhere else.
    target.__aiPlannerClientActions = () => readClientActionLog();
    // The wedding context the last turn sent to the coach.
    target.__aiPlannerWeddingContext = () => weddingContextRef.current;
    return () => {
      delete target.__aiPlannerClearThread;
      delete target.__aiPlannerDebugState;
      delete target.__aiPlannerClientActions;
      delete target.__aiPlannerWeddingContext;
    };
  }, [queryClient, setMessages, weddingId]);

  const sendMessage = useCallback<Helpers['sendMessage']>(
    (message, options) => {
      // Cleared per turn: a turn that writes nothing sends no receipt, so an old one would
      // show under a later reply.
      setTurnReceipt(null);
      // Stamps a new message. No message means the turn continues.
      return send(message ? withCreatedAt(message, Date.now()) : message, options);
    },
    [send],
  );

  const startCheckin = useCallback(
    (chipId?: string) => {
      void sendMessage({
        text: COACH_OPENER_MESSAGE,
        metadata: {
          coachIntent: { trigger: 'opener' },
          ...(chipId ? { suggestionId: chipId } : {}),
        },
      });
    },
    [sendMessage],
  );

  const retryContext = useCallback(() => {
    for (const key of ['coach-state', 'country-costs', 'country-groups']) {
      void queryClient.invalidateQueries(['ai-planner', key]);
    }
    // Dispatched by hand: the remount only fetches the template when the slice is empty,
    // so a failed listener would not run again. Both also clear the stored failure.
    dispatch({ type: ChecklistActionTypes.FETCH_TASKS_INITIAL });
    dispatch({ type: BudgetActionTypes.INIT_LISTENER });
    // The attempt is the sync component's key, so this remounts it and drops its local state.
    setContext((current) => ({ ...current, failed: false, attempt: current.attempt + 1 }));
  }, [dispatch, queryClient]);

  // After signup the coach speaks first, but only once the history has loaded and is empty.
  // A failed read also looks empty, and an opener sent then would block the real thread.
  // If a thread exists, the kickoff is dropped.
  const kickoffPending = useSelector(getIsAIPlannerKickoffPending);
  useEffect(() => {
    if (!kickoffPending || !history.loaded || status !== 'ready' || !contextReady) return;
    dispatch(setAIPlannerKickoffPending(false));
    // The stored thread counts too: it may not have reached the chat list yet
    if (messages.length === 0 && history.messages.length === 0) startCheckin();
  }, [
    kickoffPending,
    history.loaded,
    history.messages.length,
    status,
    contextReady,
    messages.length,
    dispatch,
    startCheckin,
  ]);

  const value = useMemo<AIPlannerContextValue>(
    () => ({
      sendMessage,
      setMessages,
      status,
      error,
      stop,
      addToolResult,
      regenerate,
      editLastMessage,
      restoredDraft,
      clearRestoredDraft,
      historyLoading,
      hasMessages,
      contextReady,
      contextFailed,
      contextLoading,
      retryContext,
      startCheckin,
      getMessages,
      getWeddingContext,
      isLiveToolCall,
      getToolCallDecision,
      recordToolCallDecision,
      settleClientActions,
      turnReceipt,
      chatId: chatIdRef.current,
      rateLimit,
      getCarouselScroll,
      setCarouselScroll,
    }),
    [
      sendMessage,
      setMessages,
      status,
      error,
      stop,
      addToolResult,
      regenerate,
      editLastMessage,
      restoredDraft,
      clearRestoredDraft,
      historyLoading,
      hasMessages,
      contextReady,
      contextFailed,
      contextLoading,
      retryContext,
      startCheckin,
      getMessages,
      getWeddingContext,
      isLiveToolCall,
      getToolCallDecision,
      recordToolCallDecision,
      settleClientActions,
      turnReceipt,
      rateLimit,
      getCarouselScroll,
      setCarouselScroll,
    ],
  );
  const messagesValue = useMemo<AIPlannerMessagesValue>(
    () => ({ messages, interruptedRequestIds }),
    [messages, interruptedRequestIds],
  );

  return (
    <AIPlannerCtx.Provider value={value}>
      <AIPlannerMessagesCtx.Provider value={messagesValue}>
        {engaged && (
          <WeddingContextSync
            key={contextAttempt}
            target={weddingContextRef}
            checklistTarget={resolvedChecklistRef}
            onStatusChange={onContextStatus}
          />
        )}
        {children}
      </AIPlannerMessagesCtx.Provider>
    </AIPlannerCtx.Provider>
  );
};

export const useActivePlannerChat = (): AIPlannerContextValue => {
  const ctx = useContext(AIPlannerCtx);
  if (!ctx) {
    throw new Error('useActivePlannerChat must be used inside AIPlannerProvider');
  }
  return ctx;
};

// The streamed messages. Consumers re-render on every chunk.
export const usePlannerMessages = (): AIPlannerMessagesValue => {
  const ctx = useContext(AIPlannerMessagesCtx);
  if (!ctx) {
    throw new Error('usePlannerMessages must be used inside AIPlannerProvider');
  }
  return ctx;
};
